import React, { FC } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { Features } from 'generated/featureDefinitions'
import {
  ContractFlowCustomFieldFragment,
  ContractFlowCustomFieldValueFragment,
  ContractVersionsPageDocument,
  FixContractsContractVersionDocument,
  FixFlowProviderDocument,
  PayrollIntegrationsBaseA3innuvaCodesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { ValidationError } from 'modules/core/lib/errors'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'
import type { FlashType } from 'modules/core/store/models/app'

import { manageUpperLimit } from 'modules/contracts/lib/amountStrategyUtils'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getEditedCustomFields } from 'modules/contracts/lib/customFields'

import { getFixFlowForm } from './lib/fixFlowForm'

interface FixFlowContextProps {
  form: Form
  onSubmit: (onSuccess: () => void) => void
  canEdit: boolean
  isSubmitting: boolean
  loading: boolean
  customValues: ContractFlowCustomFieldValueFragment[]
  customFields: ContractFlowCustomFieldFragment[]
}

interface FixFlowProviderProps {
  contractVersionId: number
  legalEntityId: number
  fieldIds?: number[]
  children: React.ReactNode
}

const FixFlowProviderContext = React.createContext<FixFlowContextProps | null>(null)

export const FixFlowProvider: FC<FixFlowProviderProps> = observer(
  ({ contractVersionId, legalEntityId, fieldIds, children }) => {
    const apolloClient = useApolloClient()
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const { compensations, additionalCompensations, setCustomFields, setCustomValues } =
      useContractFlow()
    const { data, loading: loadingFixFlow } = useQuery(FixFlowProviderDocument, {
      variables: {
        contractVersionId: contractVersionId,
        legalEntityId: legalEntityId,
        featureAvailabilityIds: [Features.compensations_management],
      },
    })
    const contractVersion = data?.contracts.contractVersion
    const employee = contractVersion?.employee
    const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes ?? []
    const hasCompensations =
      featureAvailabilities.find(
        (featureAvailability) => featureAvailability.id === Features.compensations_management
      )?.available ?? false
    const canEdit = contractVersion?._policies?.edit.can ?? false
    const customFieldsData = data?.companies.legalEntitiesConnection?.nodes[0]?.customFields ?? []
    const customFields = customFieldsData.map((node) => node.field).filter(Boolean) ?? []
    const customValues = data?.contracts.contractVersion?.values ?? []

    const [fixContractsContractVersion] = useMutation(FixContractsContractVersionDocument, {
      extractorFn: (mutationData) => mutationData.contractsMutations.fixContractsContractVersion,
    })

    // TODO: Regarding the integrations installed, we should only fetch the ones that applied to those integrations, but for now we fetch always the ones for a3innuva
    const { data: baseCodesData, loading: baseCodesLoading } = useQuery(
      PayrollIntegrationsBaseA3innuvaCodesDocument,
      {
        variables: {
          codeableId: contractVersion?.esAgreementCodeId,
          codeableType: 'Contracts::Spain::AgreementCode',
        },
        skip: !contractVersion?.esAgreementCodeId,
      }
    )
    const baseCodes = baseCodesData?.payrollIntegrationsBase?.codesConnection?.nodes ?? []

    const form = useUnsafeMemo(() => {
      return contractVersion
        ? getFixFlowForm({
            contractVersion,
            customFields,
            customValues,
            canSeeJobTitle: contractVersion?._policies?.seeJobTitle.can ?? false,
            canSeeStartDate: contractVersion?._policies?.seeStartDate.can ?? false,
            canSeeEndDate: contractVersion?._policies?.seeEndDate.can ?? false,
            canSeeTrialPeriod: contractVersion?._policies?.seeTrialPeriod.can ?? false,
            canSeeTrialPeriodEndDate:
              contractVersion?._policies?.seeTrialPeriodEndDate.can ?? false,
            canSeeGrossSalary: contractVersion?._policies?.seeGrossSalary.can ?? false,
            canSeeWorkingHours: contractVersion?._policies?.seeWorkingHours.can ?? false,
            baseCodes,
          })
        : null
    }, [loadingFixFlow, customValues, contractVersion, baseCodesLoading])

    useEffect(() => {
      if (!loadingFixFlow) {
        setCustomFields(
          fieldIds ? customFields.filter((field) => fieldIds?.includes(field.id)) : customFields
        )
        setCustomValues(customValues)
      }
    }, [loadingFixFlow])

    if (loadingFixFlow || baseCodesLoading) {
      return (
        <Box width="full" height="full" justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      )
    }

    if (!contractVersion || !form || !employee) return null

    const showFlashMessage = (type: FlashType, message: string): void => {
      app.setFlash({
        type,
        message,
      })
    }

    const onSubmit = async (onSuccess: () => void) => {
      if (!contractVersion.id) {
        return
      }

      setIsSubmitting(true)
      const api = ApiClient()
      form.get('payroll_policy_id').set(contractVersion.payrollPolicy?.policy?.id)
      const data = form.serialized
      delete data['es_tariff_group_name']

      // Filter out boolean fields with empty string values (null values from backend that weren't edited)
      // This prevents sending invalid data for fields the user doesn't have permission to see/edit
      // Boolean fields in factorial-form convert null to "" which fails backend validation
      const booleanFields = ['es_has_teleworking_contract', 'fr_forfait_jours', 'has_trial_period']
      booleanFields.forEach((field) => {
        if (data[field] === '') {
          delete data[field]
        }
      })

      data.employee_id = employee.id
      data.additional_compensations = compensations?.map((compensation) => compensation.attributes)
      data.custom_fields = getEditedCustomFields(form, customFields)

      if (!data.has_trial_period) data.trial_period_ends_on = null

      setIsSubmitting(true)
      if (hasCompensations) {
        const handled = handleResult(
          // TODO: add integration specs when setup supports it
          await fixContractsContractVersion({
            refetchQueries: [ContractVersionsPageDocument, FixFlowProviderDocument],
            awaitRefetchQueries: true,
            variables: {
              additionalCompensations: compensations?.map((compensation) => {
                const attributes = compensation.attributes
                return {
                  amount: attributes.amount,
                  calculation: attributes.calculation,
                  compensationType: attributes.compensation_type,
                  contractsTaxonomyId: attributes.contracts_taxonomy_id,
                  description: attributes.description,
                  firstPaymentOn: attributes.first_payment_on,
                  id: attributes.id,
                  minimumAmountOfHours: attributes.minimum_amount_of_hours,
                  minimumAmountOfHoursInCents: attributes.minimum_amount_of_hours_in_cents,
                  payrollPolicyId: attributes.payroll_policy_id,
                  recurrence: attributes.recurrence,
                  recurrenceCount: attributes.recurrence_count,
                  startsOn: attributes.starts_on,
                  syncWithSupplements: attributes.sync_with_supplements,
                  timeCondition: attributes.time_condition,
                  unit: attributes.unit,
                }
              }),
              newAdditionalCompensations: additionalCompensations?.map(({ tmpID: _, ...rest }) => {
                return {
                  id: rest.id,
                  amount: rest.amountStrategy?.amount,
                  amountStrategyType: rest.amountStrategyType,
                  contractVersionId: rest.contractVersionId,
                  description: rest.description,
                  firstPaymentOn: rest.firstPaymentOn,
                  payrollConceptId: rest.payrollConceptId,
                  recurrence: rest.recurrence,
                  upperLimit: manageUpperLimit(rest.amountStrategy),
                }
              }),
              customFields: data.custom_fields,
              employeeId: employee.id,
              id: contractVersion.id,
              contractsEsTariffGroupId: data.contracts_es_tariff_group_id,
              deActivityType: data.de_activity_type,
              deContractTypeId: data.de_contract_type_id,
              deEmploymentType: data.de_employment_type,
              deFlatRateTax: data.de_flat_rate_tax,
              dePersonalKeyGroupId: data.de_personal_key_group_id,
              effectiveOn: data.effective_on,
              endsOn: data.ends_on,
              esA3innuvaJobPositionId: data.es_a3innuva_job_position_id,
              esA3innuvaJobPositionPayrollIntegrationCode:
                data.es_a3innuva_job_position_payroll_integration_code,
              esA3innuvaJobPositionPayrollIntegrationName:
                data.es_a3innuva_job_position_payroll_integration_name,
              esAgreementCodePayrollIntegrationCode:
                data.es_agreement_code_payroll_integration_code,
              esAgreementCodePayrollIntegrationName:
                data.es_agreement_code_payroll_integration_name,
              esClassificationId: data.es_classification_id,
              esClassificationPayrollIntegrationCode:
                data.es_classification_payroll_integration_code,
              esClassificationPayrollIntegrationName:
                data.es_classification_payroll_integration_name,
              esCnoOccupationId: data.es_cno_occupation_id,
              esCnoOccupationPayrollIntegrationCode:
                data.es_cno_occupation_payroll_integration_code,
              esCnoOccupationPayrollIntegrationName:
                data.es_cno_occupation_payroll_integration_name,
              esContractObservations: data.es_contract_observations,
              esContractTypeId: data.es_contract_type_id,
              esContractTypePayrollIntegrationCode: data.es_contract_type_payroll_integration_code,
              esContractTypePayrollIntegrationName: data.es_contract_type_payroll_integration_name,
              esContributionTypeId: data.es_contribution_type_id,
              esContributionTypePayrollIntegrationCode:
                data.es_contribution_type_payroll_integration_code,
              esContributionTypePayrollIntegrationName:
                data.es_contribution_type_payroll_integration_name,
              esCotizationGroup: data.es_cotization_group,
              esEducationLevelId: data.es_education_level_id,
              esEducationLevelPayrollIntegrationCode:
                data.es_education_level_payroll_integration_code,
              esEducationLevelPayrollIntegrationName:
                data.es_education_level_payroll_integration_name,
              esHasTeleworkingContract: data.es_has_teleworking_contract,
              esJobDescription: data.es_job_description,
              esOccupationCodeId: data.es_occupation_code_id,
              esOccupationCodePayrollIntegrationCode:
                data.es_occupation_code_payroll_integration_code,
              esOccupationCodePayrollIntegrationName:
                data.es_occupation_code_payroll_integration_name,
              esProfessionalCategoryId: data.es_professional_category_id,
              esRegimeId: data.es_regime_id,
              esRegimePayrollIntegrationCode: data.es_regime_payroll_integration_code,
              esRegimePayrollIntegrationName: data.es_regime_payroll_integration_name,
              esTariffGroupId: data.es_tariff_group_id ? String(data.es_tariff_group_id) : null,
              esWorkingDayTypeId: data.es_working_day_type_id,
              frCoefficient: data.fr_coefficient,
              frContractTypeId: data.fr_contract_type_id,
              frEmployeeType: data.fr_employee_type,
              frForfaitJours: data.fr_forfait_jours,
              frJoursParAn: data.fr_jours_par_an,
              frJoursParAnCents: data.fr_jours_par_an_cents,
              frLevelId: data.fr_level_id,
              frMutualId: data.fr_mutual_id,
              frProfessionalCategoryId: data.fr_professional_category_id,
              frStepId: data.fr_step_id,
              frWorkTypeId: data.fr_work_type_id,
              hasTrialPeriod: data.has_trial_period,
              jobCatalogLevelId: data.job_catalog_level_id,
              jobCatalogTreeNodeUuid: data.job_catalog_tree_node_uuid,
              jobTitle: data.job_title,
              maxLegalYearlyHours: data.max_legal_yearly_hours,
              maximumWeeklyHours: data.maximum_weekly_hours,
              minRestMinutesBetweenDays: data.min_rest_minutes_between_days,
              maxWorkMinutesPerDay: data.max_work_minutes_per_day,
              ptContractTypeId: data.pt_contract_type_id,
              salaryAmount: data.salary_amount,
              salaryFrequency: data.salary_frequency,
              startsOn: data.starts_on,
              trialPeriodEndsOn: data.trial_period_ends_on,
              workingHours: data.working_hours,
              workingHoursFrequency: data.working_hours_frequency,
              workingWeekDays: data.working_week_days,
              workingTimePercentageInCents: data.working_time_percentage_in_cents,
              bankHolidayTreatment: data.bank_holiday_treatment,
              annualWorkingTimeDistribution: data.annual_working_time_distribution,
            },
          }),
          (errors) => {
            form.setErrors(errors)
            showFlashMessage(
              'wrong',
              Object.values(errors).join(', ') ?? i18n.t('contract_versions.flows.errors.unknown')
            )
            setIsSubmitting(false)
          }
        )

        if (handled.success) {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.fix.confirmation', {
              employeeFullName: employee.fullName,
            })
          )
          form.cleanAll()
          setIsSubmitting(false)
          onSuccess()
        }

        return handled
      } else {
        api
          .post(`/api/rest/contracts/contract_versions/fix`, data)
          .then(() => {
            apolloClient.refetchQueries({
              include: [ContractVersionsPageDocument, FixFlowProviderDocument],
            })
            showFlashMessage(
              'correct',
              i18n.t('contract_versions.flows.fix.confirmation', {
                employeeFullName: employee.fullName,
              })
            )
            onSuccess()
          })
          .catch((e) => {
            if (e instanceof ValidationError) showFlashMessage('wrong', e.message)
            else showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
          })
          .finally(() => {
            form.cleanAll()
            setIsSubmitting(false)
          })
      }
    }

    return (
      <FixFlowProviderContext.Provider
        value={{
          form,
          canEdit,
          onSubmit,
          isSubmitting,
          loading: loadingFixFlow,
          customFields: customFields,
          customValues: customValues,
        }}
      >
        {children}
      </FixFlowProviderContext.Provider>
    )
  }
)

export const useFixFlowProvider = (): FixFlowContextProps => {
  const fixFlow = React.useContext(FixFlowProviderContext)

  if (!fixFlow) {
    throw new Error('useFixFlowProvider must be used within a FixFlowProvider')
  }

  return fixFlow
}
